import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { trackRumAction } from '@/shared/observability/rum';
import { computeClampedTkResizeHeight } from '../utils/tk-resize-guard';

const INTAKE_FORM_LOAD_TIMEOUT_MS = 10_000;

type IntakeFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: string;
  url: string;
};

type IntakeFormLoadStatus = 'loading' | 'loaded' | 'error';

type IntakeFormContentProps = Pick<IntakeFormModalProps, 'providerId' | 'url'>;

function IntakeFormContent({ providerId, url }: IntakeFormContentProps) {
  const [status, setStatus] = useState<IntakeFormLoadStatus>('loading');
  const [iframeHeight, setIframeHeight] = useState<number | null>(null);
  const iframeLoadedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (iframeLoadedRef.current) return;

      trackRumAction('intake_form_load_timeout', {
        providerId,
        timestamp: Date.now(),
      });
      setStatus('error');
    }, INTAKE_FORM_LOAD_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [providerId]);

  // The Key posts `tk-resize` because the iframe has scrolling="no". Apply the
  // clamped height so the modal wrapper (overflow-auto + min-h-0) can scroll
  // to the Next button. Guard still ignores wrong origin/shape.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const clamped = computeClampedTkResizeHeight(event, true);
      if (clamped !== null) setIframeHeight(clamped);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleIframeLoad = () => {
    iframeLoadedRef.current = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setStatus('loaded');
  };

  if (status === 'error') {
    return (
      <div className="flex flex-1 items-center justify-center px-6 pb-6 text-center">
        <p>The form is unavailable right now. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto px-6 pb-6">
      <iframe
        src={url}
        width="100%"
        title="Provider Intake Form"
        className="w-full rounded-lg border-0"
        style={{ height: iframeHeight ?? 900 }}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="fullscreen"
        scrolling="no"
        onLoad={handleIframeLoad}
      />
    </div>
  );
}

/**
 * Feature 103 (The Key pilot) — embeds a provider's intake form via iframe.
 *
 * Fixed 90vh modal with internal scroll. Iframe height comes from The Key's
 * `tk-resize` postMessage (clamped 200–2000px). Open-only content remounts on
 * every reopen, so timeout and iframe state always start fresh.
 */
export function IntakeFormModal({ open, onOpenChange, providerId, url }: IntakeFormModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <DialogContent className="flex h-[90vh] max-w-4xl flex-col overflow-hidden p-0">
          <DialogHeader className="flex-shrink-0 px-6 pb-4 pt-6">
            <DialogTitle>Get Started</DialogTitle>
            <DialogDescription>Fill out the intake form below</DialogDescription>
          </DialogHeader>
          <IntakeFormContent key={`${providerId}:${url}`} providerId={providerId} url={url} />
        </DialogContent>
      )}
    </Dialog>
  );
}
